import { CheckCircle2, Circle, X, Clock, Sparkles, ChevronRight } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useOnboarding } from './useOnboarding';
import type { SectionKey } from './types';

const T = {
  sv: {
    title: 'Välj en guidning',
    subtitle: 'Varje sektion tar 1–3 minuter och fäller ut rätt panel åt dig.',
    close: 'Stäng',
    completed: 'Klar',
    minutes: 'min',
    recommended: 'Rekommenderad',
  },
  en: {
    title: 'Pick a guided tour',
    subtitle: 'Each section takes 1–3 minutes and opens the right panel for you.',
    close: 'Close',
    completed: 'Done',
    minutes: 'min',
    recommended: 'Recommended',
  },
};

export function TourPicker() {
  const ctx = useOnboarding();
  const t = T[ctx.lang === 'sv' ? 'sv' : 'en'];

  const handlePick = (s: SectionKey) => {
    ctx.setPickerVisible(false);
    ctx.startTour(s);
  };

  return (
    <Dialog open={ctx.pickerVisible} onOpenChange={(v) => ctx.setPickerVisible(v)}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{t.title}</DialogTitle>
          <p className="text-sm text-muted-foreground">{t.subtitle}</p>
        </DialogHeader>

        <div className="space-y-2 max-h-[65vh] overflow-y-auto -mx-1 px-1">
          {ctx.allSections.map((section) => {
            const completed = ctx.progress?.completed_sections.includes(section.id);
            const lang = ctx.lang === 'sv' ? 'sv' : 'en';
            const label = section.label[lang] ?? section.label.en;
            const description = section.description?.[lang] ?? section.description?.en;
            return (
              <button
                key={section.id}
                type="button"
                onClick={() => handlePick(section.id)}
                className="group w-full text-left p-4 rounded-lg border border-border hover:border-primary/60 hover:bg-muted/40 flex items-start gap-3 transition-colors"
              >
                <div className="mt-0.5">
                  {completed
                    ? <CheckCircle2 className="w-5 h-5 text-green-500" />
                    : <Circle className="w-5 h-5 text-muted-foreground" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{label}</span>
                    {section.recommended && (
                      <Badge variant="secondary" className="h-5 gap-1 text-[10px]">
                        <Sparkles className="w-3 h-3" /> {t.recommended}
                      </Badge>
                    )}
                    {completed && (
                      <Badge variant="outline" className="h-5 text-[10px]">{t.completed}</Badge>
                    )}
                  </div>
                  {description && (
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{description}</p>
                  )}
                  {section.estMinutes != null && (
                    <div className="flex items-center gap-1 mt-2 text-[11px] text-muted-foreground">
                      <Clock className="w-3 h-3" />
                      <span>~{section.estMinutes} {t.minutes}</span>
                    </div>
                  )}
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity self-center" />
              </button>
            );
          })}
        </div>

        <div className="flex items-center justify-end gap-2 pt-3 border-t border-border">
          <Button variant="ghost" size="sm" onClick={() => ctx.setPickerVisible(false)}>
            <X className="w-4 h-4 mr-1" /> {t.close}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
